import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { handleError } from '../lib/errors.js';
import { workspaceLinks, workspaceLink } from '../lib/ui-links.js';
import type { Workspace } from '../types/index.js';

// Command option interfaces for type safety
interface ListOptions {
  limit: string;
  offset?: string;
}

export function createWorkspacesCommand(): Command {
  const workspaces = new Command('workspaces')
    .description('View workspaces (read-only)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent workspaces list
  ${chalk.dim('$')} mutagent workspaces get <workspace-id>

Subcommands:
  list, get

Note: Workspace management (create, update, delete) is available in the Admin Panel only.
    `);

  // =========================================================================
  // WORKSPACE READ-ONLY OPERATIONS
  // Create, update, delete are available in Admin Panel only
  // =========================================================================

  // List workspaces
  workspaces
    .command('list')
    .description('List all workspaces')
    .option('-l, --limit <n>', 'Limit results', '50')
    .option('-o, --offset <n>', 'Offset for pagination')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent workspaces list
  ${chalk.dim('$')} mutagent workspaces list --limit 10 --json
    `)
    .action(async (options: ListOptions) => {
      const isJson = getJsonFlag(workspaces);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const filters: { limit?: number; offset?: number } = {
          limit: parseInt(options.limit, 10) || 50,
        };

        if (options.offset) {
          filters.offset = parseInt(options.offset, 10);
        }

        const result = await client.listWorkspaces(filters);

        if (isJson) {
          // Summary view — only IDs and key fields. Use `workspaces get <id>` for full details.
          const withLinks = result.data.map((w: Workspace) => ({
            id: w.id,
            name: w.name,
            slug: w.slug,
            isDefault: w.isDefault,
            updatedAt: w.updatedAt,
            _links: workspaceLinks(w.id),
          }));
          output.output({ ...result, data: withLinks });
        } else {
          // Format for table
          const formatted = result.data.map((w: Workspace) => ({
            id: w.id,
            name: w.name,
            slug: w.slug,
            isDefault: w.isDefault ? 'Yes' : 'No',
            updated: w.updatedAt ? new Date(w.updatedAt).toLocaleDateString() : 'N/A',
            url: workspaceLink(w.id),
          }));
          output.output(formatted);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Get workspace
  workspaces
    .command('get')
    .description('Get workspace details')
    .argument('<id>', 'Workspace ID')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent workspaces get <workspace-id>
  ${chalk.dim('$')} mutagent workspaces get <workspace-id> --json
    `)
    .action(async (id: string) => {
      const isJson = getJsonFlag(workspaces);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const workspace = await client.getWorkspace(id);

        if (isJson) {
          output.output({ ...workspace, _links: workspaceLinks(workspace.id) });
        } else {
          // Format for display
          const formatted: Record<string, unknown> = {
            id: workspace.id,
            name: workspace.name,
            slug: workspace.slug,
            description: workspace.description ?? 'N/A',
            isDefault: workspace.isDefault ? 'Yes' : 'No',
            createdBy: workspace.createdBy ?? 'N/A',
            createdAt: workspace.createdAt ? new Date(workspace.createdAt).toLocaleString() : 'N/A',
            updatedAt: workspace.updatedAt ? new Date(workspace.updatedAt).toLocaleString() : 'N/A',
            url: workspaceLink(workspace.id),
          };
          output.output(formatted);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  return workspaces;
}
